import React, { useState } from 'react';
import styled from 'styled-components';
import { NumericCodeControlProps } from '../models';
import { inputStyle, activeInputStyle } from '../styles';

const segmentGutter = 10;

const s = {
  Container: styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  `,
  Segment: styled.div<{ hasError: boolean; totalSegments: number; isActive?: boolean }>`
    ${inputStyle};
    display: flex;
    cursor: text;
    box-sizing: border-box;
    width: calc(${(props) => 100 / props.totalSegments}% - ${segmentGutter}px);
    align-items: center;
    justify-content: center;
    ${(props) => props.isActive && activeInputStyle};
  `,
  Input: styled.input<{ valueLength: number; maxLength: number }>`
    outline: none;
    font-size: 16px;
    position: absolute;
    user-select: none;
    caret-color: #000;
    border: none;
    background: transparent;
    padding: 0;
    margin: 0;
    width: 1px;
    text-align: right;
    color: transparent;
    left: ${({ valueLength, maxLength }) => {
      const isLast = valueLength === maxLength;
      /* The last index would push the cursor off the input */
      const segmentIndex = isLast ? valueLength - 1 : valueLength;
      return `calc(
        /* Move to the left edge of where the segment would be if there were no gutters between segments */
        ${(segmentIndex / maxLength) * 100}%
        /* Move to the center of the segment */
        + ${(1 / maxLength / 2) * 100}%
        /* Correct for the presence of space-between gutters */
        + ${(segmentGutter / (maxLength - 1)) * segmentIndex}px
        /* Correct constant offset created by the gutter width */
        - ${segmentGutter / 2}px
        /* For the last segment, move right by half an em to make it appear immediately after the character */
        + ${isLast ? 0.5 : 0}em
      )`;
    }};
  `,
};

const NumericCodeControl: React.FC<NumericCodeControlProps> = (props) => {
  const { ...inputProps } = props;
  const maxLength = props.maxLength || 6;
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = React.createRef<HTMLInputElement>();
  const segments: Array<React.ReactElement> = [];
  const valueChars = props.value ? String(props.value).split('') : [];
  for (let i = 0; i < maxLength; i++) {
    const currentLength = valueChars.length;
    segments.push(
      <s.Segment
        hasError={props.hasError}
        totalSegments={maxLength}
        onClick={() => inputRef.current && inputRef.current.focus()}
        key={i}
        isActive={isFocused && Math.min(currentLength, maxLength - 1) === i}
      >
        {valueChars[i] || <>&nbsp;</>}
      </s.Segment>
    );
  }

  const resetCursor = () => {
    if (inputRef.current) {
      inputRef.current.setSelectionRange(maxLength, maxLength);
    }
  };

  const allInputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    ...inputProps,
    maxLength,
    onKeyDown: (e) => {
      resetCursor();
      props.onKeyDown && props.onKeyDown(e);
    },
    onKeyUp: (e) => {
      resetCursor();
      props.onKeyUp && props.onKeyUp(e);
    },
    onBlur: (e) => {
      setIsFocused(false);
      props.onBlur && props.onBlur(e);
    },
    onChange: (e) => {
      const isOnlyNumbers = Boolean(e.target.value.match(/^[0-9]*$/));
      resetCursor();
      if (isOnlyNumbers && props.onChange) {
        props.onChange(e);
      }
    },
    onFocus: (e) => {
      setIsFocused(true);
      resetCursor();
      props.onFocus && props.onFocus(e);
    },
  };

  const additionalProps = {
    valueLength: valueChars.length,
    ref: inputRef,
  };

  return (
    <s.Container>
      <s.Input autoFocus {...allInputProps} {...additionalProps} maxLength={maxLength} />
      {segments}
    </s.Container>
  );
};

export default NumericCodeControl;



// WEBPACK FOOTER //
// ./packages/smart-home-support/components/forms/controls/NumericCodeControl.tsx